"use client";

import {
  Chip,
  type ChipOption,
  SubmittedChips,
  shouldStackChips,
  Typography,
  toggleChipSelection,
  useStylesConfigValue,
} from "@health/ui";
import { cn } from "@health/ui/utils";
import { useEffect, useRef, useState } from "react";
import { useAnalyticsContext } from "../analytics/context";
import { useTrack } from "../analytics/useTrack";
import { useTurnTiming } from "../analytics/useTurnTiming";
import { ChipsWidgetSkeleton } from "./ChipsWidget.skeleton";

type Question = {
  id: string;
  question: string;
  options: ChipOption[];
  multi_select?: boolean;
  include_fallback?: boolean;
};

type Answer = {
  id: string;
  selected: string[];
  fallback_selected: boolean;
};

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  demoMode?: boolean;
  streaming?: boolean;
};

export function ChipGroupWidget({ input, output, onSubmit, submitted, streaming }: Props) {
  const questions = ((input.questions as Question[]) ?? [])
    .filter((q) => q.id && q.question)
    .map((q) => ({
      ...q,
      options: [
        ...(q.options ?? []).filter((opt) => opt.id && opt.label),
        ...(!streaming && (q.include_fallback ?? true)
          ? [{ id: "__fallback__", label: "Something else" }]
          : []),
      ],
    }));

  // Track selections per question: questionId -> Set<optionId>
  const [selections, setSelections] = useState<Record<string, Set<string>>>(() => {
    const init: Record<string, Set<string>> = {};
    for (const q of questions) {
      init[q.id] = new Set();
    }
    return init;
  });

  const singleQuestion = questions.length === 1;

  // Focus the first interactive chip when the widget becomes ready (not
  // streaming, not submitted, first question has options). Mirrors
  // ChipsWidget so keyboard/SR users always land on the new question.
  //
  // Wave 0.3 TTFC: fused with focus landing — same rAF tick. See
  // ChipsWidget for detailed rationale.
  const rootRef = useRef<HTMLDivElement>(null);
  const didFocusRef = useRef(false);
  const firstQ = questions[0];
  const readyForFocus = !streaming && !submitted && !!firstQ && firstQ.options.length > 0;
  const { markWidgetReady, markAnyWidgetReady } = useTurnTiming();
  const analytics = useAnalyticsContext();
  const track = useTrack();
  const traceMode = useStylesConfigValue("traceMode");
  useEffect(() => {
    if (!readyForFocus || didFocusRef.current) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const raf = requestAnimationFrame(() => {
      const firstChip = rootRef.current?.querySelector<HTMLElement>(
        '[role="radio"], [role="checkbox"], button',
      );
      if (!firstChip) return;
      didFocusRef.current = true;
      firstChip.focus({ preventScroll: true });
      const variantId = analytics?.variantId;
      const brand = analytics?.site ? { brand: analytics.site } : {};
      const traceModeTag =
        traceMode === "plan" || traceMode === "disabled" || traceMode === "deliberate"
          ? { traceMode }
          : {};

      const result = markWidgetReady({ widgetType: "chip-group" });
      if (result && variantId) {
        track("latency:time_to_first_chip", {
          durationMs: result.durationMs,
          turnIndex: result.turnIndex,
          trigger: result.trigger,
          widgetType: "chip-group",
          capture_source: "browser",
          variantId,
          ...brand,
          ...traceModeTag,
        });
      }

      const anyResult = markAnyWidgetReady({ widgetType: "chip-group" });
      if (anyResult && variantId) {
        track("latency:user_to_first_widget_ms", {
          durationMs: anyResult.durationMs,
          turnIndex: anyResult.turnIndex,
          trigger: anyResult.trigger,
          widgetType: "chip-group",
          capture_source: "browser",
          variantId,
          ...brand,
          ...traceModeTag,
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [
    readyForFocus,
    markWidgetReady,
    markAnyWidgetReady,
    track,
    analytics?.variantId,
    analytics?.site,
    traceMode,
  ]);

  if (!questions.length && !streaming) return null;

  // Read-only / submitted mode
  if (submitted && output) {
    const result = output as { answers: Answer[] };
    const answersMap = new Map(result.answers?.map((a) => [a.id, a]) ?? []);

    return (
      <div className="w-full space-y-6">
        {questions.map((q) => {
          const answer = answersMap.get(q.id);
          return (
            <SubmittedChips
              key={q.id}
              question={q.question}
              allOptions={q.options}
              selectedIds={new Set(answer?.selected ?? [])}
              fallbackSelected={answer?.fallback_selected ?? false}
              multiSelect={q.multi_select ?? false}
            />
          );
        })}
      </div>
    );
  }

  // Fallback for submitted without output
  if (submitted) {
    return <div className="w-full text-sm text-text-tertiary italic">Completed</div>;
  }

  // Streaming mode: disabled chips, no Continue button. Keep the outer
  // wrapper mounted across the empty -> first-chip transition so React
  // reconciles in place (no skeleton unmount/remount flash). Question label
  // (`opt.id && opt.label`) is enforced by the upstream filter, so
  // `options.length > 0` already implies "first chip is fully formed".
  if (streaming) {
    const firstQuestion = questions[0];
    const showSkeleton = !firstQuestion || firstQuestion.options.length === 0;

    if (showSkeleton) {
      return (
        <div className="w-full" role="group" aria-busy aria-label="Loading question">
          <ChipsWidgetSkeleton />
        </div>
      );
    }

    return (
      <div className="w-full space-y-6">
        {questions.map((q) => {
          const isMulti = q.multi_select ?? false;
          const shouldStack = shouldStackChips(q);

          return (
            <div key={q.id} className="space-y-3">
              {!singleQuestion && <Typography.AgentText>{q.question}</Typography.AgentText>}
              <div
                role={isMulti ? "group" : "radiogroup"}
                aria-label={q.question}
                aria-busy
                className={cn(
                  "flex flex-wrap gap-2",
                  (isMulti || shouldStack) && "flex-col gap-2.5",
                )}
              >
                {q.options.map((opt) => (
                  <Chip
                    key={opt.id}
                    label={opt.label}
                    disabled
                    variant={isMulti ? "multi-select" : "single-select"}
                    role={isMulti ? "checkbox" : "radio"}
                    aria-checked={false}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const handleSelect = (questionId: string, optionId: string, isMultiSelect: boolean) => {
    if (isMultiSelect) {
      setSelections((prev) => {
        const question = questions.find((q) => q.id === questionId);
        if (!question) return prev;
        return {
          ...prev,
          [questionId]: toggleChipSelection(prev[questionId], optionId, question.options),
        };
      });
    } else {
      setSelections((prev) => ({ ...prev, [questionId]: new Set([optionId]) }));
    }
  };

  const allAnswered = questions.every((q) => (selections[q.id]?.size ?? 0) > 0);

  const handleSubmit = () => {
    const answers: Answer[] = questions.map((q) => {
      const sel = selections[q.id] ?? new Set();
      const fallbackSelected = sel.has("__fallback__");
      const selectedIds = Array.from(sel).filter((id) => id !== "__fallback__");
      return {
        id: q.id,
        selected: selectedIds,
        fallback_selected: fallbackSelected,
      };
    });
    onSubmit({ answers });
  };

  return (
    <div ref={rootRef} className="w-full space-y-6">
      {questions.map((q) => {
        const isMulti = q.multi_select ?? false;
        const selected = selections[q.id] ?? new Set();
        const shouldStack = shouldStackChips(q);

        return (
          <div key={q.id} className="space-y-3">
            {!singleQuestion && <Typography.AgentText>{q.question}</Typography.AgentText>}
            {isMulti && <p className="text-sm text-text-agent">(Select all that apply)</p>}
            {isMulti ? (
              <div role="group" aria-label={q.question} className="space-y-2.5">
                {q.options.map((opt) => {
                  const isSelected = selected.has(opt.id);
                  const isFallback = opt.id === "__fallback__";
                  return (
                    <Chip
                      key={opt.id}
                      label={opt.label}
                      selected={isSelected}
                      secondary={isFallback}
                      variant="multi-select"
                      role="checkbox"
                      aria-checked={isSelected}
                      onClick={() => handleSelect(q.id, opt.id, true)}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                role="radiogroup"
                aria-label={q.question}
                className={cn(
                  "flex items-start flex-wrap gap-2",
                  shouldStack && "flex-col gap-2.5",
                )}
              >
                {q.options.map((opt) => {
                  const isSelected = selected.has(opt.id);
                  const isFallback = opt.id === "__fallback__";
                  return (
                    <Chip
                      key={opt.id}
                      label={opt.label}
                      selected={isSelected}
                      secondary={isFallback}
                      variant="single-select"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => handleSelect(q.id, opt.id, false)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <button
        onClick={handleSubmit}
        disabled={!allAnswered}
        aria-disabled={!allAnswered}
        className={cn(
          "mt-4 block w-content rounded-full py-4 px-9 text-sm font-semibold text-white transition-all active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          allAnswered
            ? "cursor-pointer bg-checkbox-filled hover:bg-accent/80"
            : "cursor-not-allowed bg-secondary/30 text-text-tertiary",
        )}
      >
        Continue
      </button>
    </div>
  );
}
